/**
 * Typed endpoint wrappers, grouped by the route file that implements them.
 *
 * HONESTY RULE: there is a wrapper here if and only if a handler exists in
 * `apps/api-worker/src/routes/*.ts`. Nothing is stubbed, nothing is invented.
 * Screens that need an endpoint that does not exist yet must render an explicit
 * "đang phát triển" placeholder rather than call a wrapper that lies.
 *
 * Endpoint paths are written relative to the client's `baseUrl`, which points
 * at `/api`. So `/v1/branches` here == `/api/v1/branches` on the wire.
 */
import type { ApiClient, RequestOptions } from './client.js';
import type {
  Area,
  AreaInput,
  AreaPatch,
  Branch,
  BranchInput,
  BranchListItem,
  BranchLocaleInput,
  BranchPatch,
  BranchPrice,
  BranchPriceInput,
  CashSessionDetail,
  ChangePasswordResponse,
  Combo,
  ComboInput,
  ComboItemInput,
  ComboPatch,
  CustomerMenu,
  Device,
  DeviceActivationResponse,
  DeviceCreatedResponse,
  DeviceInput,
  DiningTable,
  FloorMapTable,
  GlossaryTerm,
  GlossaryTermInput,
  HealthResponse,
  LocalesResponse,
  LoginRequest,
  LoginResponse,
  MeResponse,
  MenuCategory,
  MenuCategoryInput,
  MenuCategoryPatch,
  MetaResponse,
  Modifier,
  ModifierGroup,
  ModifierGroupWithModifiers,
  OkResponse,
  OrderListQuery,
  OrderListResponse,
  Printer,
  PrinterInput,
  PrinterPatch,
  Product,
  ProductAvailabilityInput,
  ProductDetail,
  ProductAllergenInput,
  ProductInput,
  ProductListResponse,
  ProductModifierGroupLinkInput,
  ProductPatch,
  ProductStationRouteInput,
  ProductVariantInput,
  RefreshResponse,
  Role,
  RolePermissionsResponse,
  RolePermissionsSaveResponse,
  Station,
  StationInput,
  StationLoad,
  StationPatch,
  SwitchBranchResponse,
  TableInput,
  TableLayoutRequest,
  TableLayoutResponse,
  TablePatch,
  TenantLocaleInput,
  TranslationDetail,
  TranslationEditRequest,
  TranslationEditResponse,
  TranslationEntityType,
  TranslationGenerateRequest,
  TranslationGenerateResponse,
  TranslationProvider,
  TranslationProviderInput,
  TranslationQueueResponse,
  TranslationStatus,
  TranslationWorkflowResponse,
} from './types.js';

/** Options a caller may add to any wrapper (abort signal, reason, …). */
type Extra = Pick<RequestOptions, 'signal' | 'reason' | 'idempotencyKey' | 'timeoutMs'>;

function extra(o: Extra | undefined): RequestOptions {
  return o === undefined ? {} : { ...o };
}

// ===========================================================================
// meta  (app.ts)
// ===========================================================================

export function metaApi(client: ApiClient) {
  return {
    /** Permission catalogue + supported locales. Unauthenticated. */
    get: (o?: Extra): Promise<MetaResponse> =>
      client.get<MetaResponse>('/v1/meta', { ...extra(o), anonymous: true }),
    health: (o?: Extra): Promise<HealthResponse> =>
      client.get<HealthResponse>('/health', { ...extra(o), anonymous: true }),
  };
}

// ===========================================================================
// auth  (routes/auth.ts)
// ===========================================================================

export function authApi(client: ApiClient) {
  return {
    login: (body: LoginRequest, o?: Extra): Promise<LoginResponse> =>
      client.post<LoginResponse>('/v1/auth/login', { ...extra(o), body, anonymous: true, idempotent: false }),

    /**
     * Normally handled inside the client on 401. Exposed for the rare explicit
     * case (an app resuming from background with a known-expired token).
     */
    refresh: (refreshToken: string, o?: Extra): Promise<RefreshResponse> =>
      client.post<RefreshResponse>('/v1/auth/refresh', {
        ...extra(o),
        body: { refreshToken },
        anonymous: true,
        idempotent: false,
      }),

    logout: (o?: Extra): Promise<OkResponse> =>
      client.post<OkResponse>('/v1/auth/logout', { ...extra(o), idempotent: false }),

    logoutAll: (o?: Extra): Promise<OkResponse> =>
      client.post<OkResponse>('/v1/auth/logout-all', { ...extra(o), idempotent: false }),

    changePassword: (
      body: { currentPassword: string; newPassword: string },
      o?: Extra,
    ): Promise<ChangePasswordResponse> =>
      client.post<ChangePasswordResponse>('/v1/auth/change-password', {
        ...extra(o),
        body,
        idempotent: false,
      }),

    /** The bootstrap payload every app calls right after login. */
    me: (o?: Extra): Promise<MeResponse> => client.get<MeResponse>('/v1/auth/me', extra(o)),

    switchBranch: (branchId: string, o?: Extra): Promise<SwitchBranchResponse> =>
      client.post<SwitchBranchResponse>('/v1/auth/switch-branch', {
        ...extra(o),
        body: { branchId },
        idempotent: false,
      }),
  };
}

// ===========================================================================
// organisation  (routes/organization.ts)
// ===========================================================================

export function orgApi(client: ApiClient) {
  return {
    // --- branches ---------------------------------------------------------
    listBranches: (
      query?: { status?: string; regionId?: string },
      o?: Extra,
    ): Promise<{ branches: BranchListItem[] }> =>
      client.get<{ branches: BranchListItem[] }>('/v1/branches', { ...extra(o), query: query ?? {} }),

    getBranch: (id: string, o?: Extra): Promise<{ branch: Branch }> =>
      client.get<{ branch: Branch }>(`/v1/branches/${encodeURIComponent(id)}`, extra(o)),

    createBranch: (body: BranchInput, o?: Extra): Promise<{ branch: Branch }> =>
      client.post<{ branch: Branch }>('/v1/branches', { ...extra(o), body }),

    updateBranch: (id: string, body: BranchPatch, o?: Extra): Promise<{ branch: Branch }> =>
      client.patch<{ branch: Branch }>(`/v1/branches/${encodeURIComponent(id)}`, { ...extra(o), body }),

    // --- areas ------------------------------------------------------------
    listAreas: (branchId: string, o?: Extra): Promise<{ areas: Area[] }> =>
      client.get<{ areas: Area[] }>(`/v1/branches/${encodeURIComponent(branchId)}/areas`, extra(o)),

    createArea: (body: AreaInput, o?: Extra): Promise<{ area: Area }> =>
      client.post<{ area: Area }>('/v1/areas', { ...extra(o), body }),

    updateArea: (id: string, body: AreaPatch, o?: Extra): Promise<{ area: Area }> =>
      client.patch<{ area: Area }>(`/v1/areas/${encodeURIComponent(id)}`, { ...extra(o), body }),

    // --- tables -----------------------------------------------------------
    /** Floor map: geometry + the live session summary each table badge needs. */
    listTables: (
      branchId: string,
      query?: { areaId?: string },
      o?: Extra,
    ): Promise<{ branchId: string; tables: FloorMapTable[] }> =>
      client.get<{ branchId: string; tables: FloorMapTable[] }>(
        `/v1/branches/${encodeURIComponent(branchId)}/tables`,
        { ...extra(o), query: query ?? {} },
      ),

    createTable: (body: TableInput, o?: Extra): Promise<{ table: DiningTable }> =>
      client.post<{ table: DiningTable }>('/v1/tables', { ...extra(o), body }),

    updateTable: (id: string, body: TablePatch, o?: Extra): Promise<{ table: DiningTable }> =>
      client.patch<{ table: DiningTable }>(`/v1/tables/${encodeURIComponent(id)}`, { ...extra(o), body }),

    /**
     * Persist a whole drag-and-drop layout in ONE call. The API applies every
     * row in a single D1 batch: a half-saved floor plan is worse than a
     * rejected save.
     */
    saveTableLayout: (body: TableLayoutRequest, o?: Extra): Promise<TableLayoutResponse> =>
      client.patch<TableLayoutResponse>('/v1/tables/layout', { ...extra(o), body }),

    // --- stations ---------------------------------------------------------
    listStations: (
      query?: { branchId?: string; activeOnly?: boolean },
      o?: Extra,
    ): Promise<{ stations: Station[] }> =>
      client.get<{ stations: Station[] }>('/v1/stations', {
        ...extra(o),
        query: { branchId: query?.branchId, activeOnly: query?.activeOnly === true ? 'true' : undefined },
      }),

    createStation: (body: StationInput, o?: Extra): Promise<{ station: Station }> =>
      client.post<{ station: Station }>('/v1/stations', { ...extra(o), body }),

    updateStation: (id: string, body: StationPatch, o?: Extra): Promise<{ station: Station }> =>
      client.patch<{ station: Station }>(`/v1/stations/${encodeURIComponent(id)}`, { ...extra(o), body }),

    // --- printers ---------------------------------------------------------
    listPrinters: (query?: { branchId?: string }, o?: Extra): Promise<{ printers: Printer[] }> =>
      client.get<{ printers: Printer[] }>('/v1/printers', { ...extra(o), query: query ?? {} }),

    createPrinter: (body: PrinterInput, o?: Extra): Promise<{ printer: Printer }> =>
      client.post<{ printer: Printer }>('/v1/printers', { ...extra(o), body }),

    updatePrinter: (id: string, body: PrinterPatch, o?: Extra): Promise<{ printer: Printer }> =>
      client.patch<{ printer: Printer }>(`/v1/printers/${encodeURIComponent(id)}`, { ...extra(o), body }),

    // --- devices ----------------------------------------------------------
    listDevices: (
      query?: { branchId?: string; status?: string; mode?: string },
      o?: Extra,
    ): Promise<{ devices: Device[] }> =>
      client.get<{ devices: Device[] }>('/v1/devices', { ...extra(o), query: query ?? {} }),

    /**
     * Registration returns the 6-character activation code EXACTLY once — the
     * server keeps only its hash. The screen must show it immediately and warn
     * that it cannot be retrieved. Requires `x-action-reason`.
     */
    registerDevice: (body: DeviceInput, reason: string, o?: Extra): Promise<DeviceCreatedResponse> =>
      client.post<DeviceCreatedResponse>('/v1/devices', { ...extra(o), body, reason }),

    activateDevice: (
      id: string,
      body: { activationCode: string; fingerprint?: string | null; platform?: string | null },
      o?: Extra,
    ): Promise<DeviceActivationResponse> =>
      client.post<DeviceActivationResponse>(`/v1/devices/${encodeURIComponent(id)}/activate`, {
        ...extra(o),
        body,
        idempotent: false,
      }),

    revokeDevice: (id: string, reason: string, o?: Extra): Promise<DeviceActivationResponse> =>
      client.post<DeviceActivationResponse>(`/v1/devices/${encodeURIComponent(id)}/revoke`, {
        ...extra(o),
        reason,
        idempotent: false,
      }),

    // --- roles ------------------------------------------------------------
    listRoles: (o?: Extra): Promise<{ roles: Role[] }> => client.get<{ roles: Role[] }>('/v1/roles', extra(o)),

    getRolePermissions: (roleId: string, o?: Extra): Promise<RolePermissionsResponse> =>
      client.get<RolePermissionsResponse>(`/v1/roles/${encodeURIComponent(roleId)}/permissions`, extra(o)),

    /** Replaces the whole set. Sensitive: requires `x-action-reason`. */
    setRolePermissions: (
      roleId: string,
      permissions: readonly string[],
      reason: string,
      rowVersion?: number | null,
      o?: Extra,
    ): Promise<RolePermissionsSaveResponse> =>
      client.put<RolePermissionsSaveResponse>(`/v1/roles/${encodeURIComponent(roleId)}/permissions`, {
        ...extra(o),
        body: { permissions, rowVersion: rowVersion ?? null },
        reason,
        idempotent: false,
      }),
  };
}

// ===========================================================================
// menu  (routes/menu.ts)
// ===========================================================================

export function menuApi(client: ApiClient) {
  return {
    listCategories: (
      query?: { branchId?: string; includeInactive?: boolean },
      o?: Extra,
    ): Promise<{ categories: MenuCategory[] }> =>
      client.get<{ categories: MenuCategory[] }>('/v1/menu/categories', {
        ...extra(o),
        query: {
          branchId: query?.branchId,
          includeInactive: query?.includeInactive === true ? 'true' : undefined,
        },
      }),

    createCategory: (body: MenuCategoryInput, o?: Extra): Promise<{ category: MenuCategory }> =>
      client.post<{ category: MenuCategory }>('/v1/menu/categories', { ...extra(o), body }),

    updateCategory: (
      id: string,
      body: MenuCategoryPatch,
      o?: Extra,
    ): Promise<{ category: MenuCategory }> =>
      client.patch<{ category: MenuCategory }>(`/v1/menu/categories/${encodeURIComponent(id)}`, {
        ...extra(o),
        body,
      }),

    listProducts: (
      query?: { page?: number; pageSize?: number; categoryId?: string; status?: string; search?: string },
      o?: Extra,
    ): Promise<ProductListResponse> =>
      client.get<ProductListResponse>('/v1/products', { ...extra(o), query: query ?? {} }),

    /** Full editor payload: variants, modifiers, allergens, routes, prices. */
    getProduct: (id: string, o?: Extra): Promise<ProductDetail> =>
      client.get<ProductDetail>(`/v1/products/${encodeURIComponent(id)}`, extra(o)),

    createProduct: (body: ProductInput, o?: Extra): Promise<{ product: Product }> =>
      client.post<{ product: Product }>('/v1/products', { ...extra(o), body }),

    updateProduct: (id: string, body: ProductPatch, o?: Extra): Promise<{ product: Product }> =>
      client.patch<{ product: Product }>(`/v1/products/${encodeURIComponent(id)}`, { ...extra(o), body }),

    publishProduct: (
      id: string,
      o?: Extra,
    ): Promise<{ ok: boolean; productId: string; status: string; publishedAt: number }> =>
      client.post<{ ok: boolean; productId: string; status: string; publishedAt: number }>(
        `/v1/products/${encodeURIComponent(id)}/publish`,
        { ...extra(o), idempotent: false },
      ),

    /** Mark a dish 86'd / restore it, per branch and business date. */
    setAvailability: (
      id: string,
      body: ProductAvailabilityInput,
      o?: Extra,
    ): Promise<{
      ok: boolean;
      productId: string;
      branchId: string;
      businessDate: string;
      isAvailable: boolean;
      availabilityId: string;
    }> =>
      client.patch(`/v1/products/${encodeURIComponent(id)}/availability`, { ...extra(o), body }),

    listModifierGroups: (
      query?: { kind?: string },
      o?: Extra,
    ): Promise<{ modifierGroups: ModifierGroupWithModifiers[] }> =>
      client.get<{ modifierGroups: ModifierGroupWithModifiers[] }>('/v1/modifier-groups', {
        ...extra(o),
        query: query ?? {},
      }),

    createModifierGroup: (
      body: {
        code: string;
        nameVi: string;
        selectionType?: 'SINGLE' | 'MULTIPLE';
        minSelect?: number;
        maxSelect?: number;
        isRequired?: boolean;
        sortOrder?: number;
        groupKind?: string;
        isActive?: boolean;
      },
      o?: Extra,
    ): Promise<{ modifierGroup: ModifierGroup }> =>
      client.post<{ modifierGroup: ModifierGroup }>('/v1/modifier-groups', { ...extra(o), body }),

    updateModifierGroup: (
      id: string,
      body: Record<string, unknown>,
      o?: Extra,
    ): Promise<{ modifierGroup: ModifierGroup }> =>
      client.patch<{ modifierGroup: ModifierGroup }>(`/v1/modifier-groups/${encodeURIComponent(id)}`, {
        ...extra(o),
        body,
      }),

    createModifier: (body: Record<string, unknown>, o?: Extra): Promise<{ modifier: Modifier }> =>
      client.post<{ modifier: Modifier }>('/v1/modifiers', { ...extra(o), body }),

    updateModifier: (
      id: string,
      body: Record<string, unknown>,
      o?: Extra,
    ): Promise<{ modifier: Modifier }> =>
      client.patch<{ modifier: Modifier }>(`/v1/modifiers/${encodeURIComponent(id)}`, { ...extra(o), body }),

    /**
     * Replaces the product's whole variant set. Send `id` on rows that already
     * exist — the handler preserves their identity (sales history references
     * variant ids) and soft-deletes rows missing from the payload.
     */
    setProductVariants: (
      productId: string,
      variants: readonly ProductVariantInput[],
      o?: Extra,
    ): Promise<{ ok: boolean; productId: string; variants: number }> =>
      client.put(`/v1/products/${encodeURIComponent(productId)}/variants`, {
        ...extra(o),
        body: { variants },
        idempotent: false,
      }),

    /**
     * Replaces the product's allergen declaration. Safety data: the server
     * stamps the HUMAN actor as `confirmed_by` — AI never infers allergens.
     */
    setProductAllergens: (
      productId: string,
      allergens: readonly ProductAllergenInput[],
      o?: Extra,
    ): Promise<{ ok: boolean; productId: string; allergens: number }> =>
      client.put(`/v1/products/${encodeURIComponent(productId)}/allergens`, {
        ...extra(o),
        body: { allergens },
        idempotent: false,
      }),

    /** Replaces the product's station routing rules (kitchen/bar dispatch). */
    setProductStationRoutes: (
      productId: string,
      routes: readonly ProductStationRouteInput[],
      o?: Extra,
    ): Promise<{ ok: boolean; productId: string; routes: number }> =>
      client.put(`/v1/products/${encodeURIComponent(productId)}/station-routes`, {
        ...extra(o),
        body: { routes },
        idempotent: false,
      }),

    /** Replaces the product's whole modifier-group link set. */
    setProductModifierGroups: (
      productId: string,
      groups: readonly ProductModifierGroupLinkInput[],
      o?: Extra,
    ): Promise<{ ok: boolean; productId: string; groups: number }> =>
      client.put(`/v1/products/${encodeURIComponent(productId)}/modifier-groups`, {
        ...extra(o),
        body: { groups },
        idempotent: false,
      }),

    listCombos: (o?: Extra): Promise<{ combos: Combo[] }> =>
      client.get<{ combos: Combo[] }>('/v1/combos', extra(o)),

    getCombo: (id: string, o?: Extra): Promise<{ combo: Combo; items: Record<string, unknown>[] }> =>
      client.get<{ combo: Combo; items: Record<string, unknown>[] }>(
        `/v1/combos/${encodeURIComponent(id)}`,
        extra(o),
      ),

    createCombo: (body: ComboInput, o?: Extra): Promise<{ combo: Combo }> =>
      client.post<{ combo: Combo }>('/v1/combos', { ...extra(o), body }),

    updateCombo: (id: string, body: ComboPatch, o?: Extra): Promise<{ combo: Combo }> =>
      client.patch<{ combo: Combo }>(`/v1/combos/${encodeURIComponent(id)}`, { ...extra(o), body }),

    setComboItems: (
      id: string,
      items: readonly ComboItemInput[],
      o?: Extra,
    ): Promise<{ ok: boolean; comboId: string; items: number }> =>
      client.put(`/v1/combos/${encodeURIComponent(id)}/items`, {
        ...extra(o),
        body: { items },
        idempotent: false,
      }),

    /** Exactly what a guest sees after scanning the QR — for review before go-live. */
    customerPreview: (
      query: { branchId: string; locale?: string; fallbackBehaviour?: string },
      o?: Extra,
    ): Promise<{ menu: CustomerMenu }> =>
      client.get<{ menu: CustomerMenu }>('/v1/menu/customer-preview', { ...extra(o), query }),

    listBranchPrices: (
      branchId: string,
      query?: { productId?: string },
      o?: Extra,
    ): Promise<{ branchId: string; prices: BranchPrice[] }> =>
      client.get<{ branchId: string; prices: BranchPrice[] }>(
        `/v1/branches/${encodeURIComponent(branchId)}/prices`,
        { ...extra(o), query: query ?? {} },
      ),

    /** Replaces this branch's OWN overrides; tenant-wide rows are left alone. */
    setBranchPrices: (
      branchId: string,
      prices: readonly BranchPriceInput[],
      o?: Extra,
    ): Promise<{ ok: boolean; branchId: string; prices: number }> =>
      client.put(`/v1/branches/${encodeURIComponent(branchId)}/prices`, {
        ...extra(o),
        body: { prices },
        idempotent: false,
      }),
  };
}

// ===========================================================================
// orders  (routes/orders.ts) — the Admin app only reads
// ===========================================================================

export function ordersApi(client: ApiClient) {
  return {
    list: (query?: OrderListQuery, o?: Extra): Promise<OrderListResponse> =>
      client.get<OrderListResponse>('/v1/orders', {
        ...extra(o),
        query: {
          branchId: query?.branchId,
          businessDate: query?.businessDate,
          status: query?.status?.join(','),
          paymentStatus: query?.paymentStatus?.join(','),
          diningSessionId: query?.diningSessionId,
          page: query?.page,
          limit: query?.limit,
        },
      }),

    getDiningSession: (id: string, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>(`/v1/dining-sessions/${encodeURIComponent(id)}`, extra(o)),
  };
}

// ===========================================================================
// payments  (routes/payments.ts)
// ===========================================================================

export function paymentsApi(client: ApiClient) {
  return {
    /**
     * NOTE: the API exposes a cash session by ID only — there is no list
     * endpoint yet. The Quỹ screen reflects that instead of faking a list.
     */
    getCashSession: (id: string, o?: Extra): Promise<CashSessionDetail> =>
      client.get<CashSessionDetail>(`/v1/cash-sessions/${encodeURIComponent(id)}`, extra(o)),
  };
}

// ===========================================================================
// production  (routes/production.ts)
// ===========================================================================

export function productionApi(client: ApiClient) {
  return {
    /** Kitchen/bar load for the realtime dashboard. Polled today. */
    stationLoad: (stationId: string, o?: Extra): Promise<StationLoad> =>
      client.get<StationLoad>(`/v1/stations/${encodeURIComponent(stationId)}/load`, extra(o)),

    stationTickets: (
      stationId: string,
      query?: { status?: string; limit?: number },
      o?: Extra,
    ): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>(`/v1/stations/${encodeURIComponent(stationId)}/tickets`, {
        ...extra(o),
        query: query ?? {},
      }),
  };
}

// ===========================================================================
// staff  (routes/staff.ts) — SELF-SERVICE ONLY.
//
// Every handler in that file is scoped to the calling employee (`/staff/home`,
// `/staff/kpi`, …). There is NO admin-side employee CRUD endpoint yet, which is
// why the Admin "Nhân viên" screens are placeholders.
// ===========================================================================

export function staffApi(client: ApiClient) {
  return {
    home: (o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/staff/home', extra(o)),
    attendance: (
      query?: { from?: string; to?: string },
      o?: Extra,
    ): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/staff/attendance', { ...extra(o), query: query ?? {} }),
    tasks: (query?: { status?: string }, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/staff/tasks', { ...extra(o), query: query ?? {} }),
    kpi: (query?: { periodStart?: string; periodEnd?: string }, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/staff/kpi', { ...extra(o), query: query ?? {} }),
    shifts: (query?: { from?: string; to?: string }, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/staff/shifts', { ...extra(o), query: query ?? {} }),
    payrollPreview: (o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/staff/payroll/preview', extra(o)),
  };
}

// ===========================================================================
// translation  (routes/translation.ts)
// ===========================================================================

export function translationApi(client: ApiClient) {
  const seg = (s: string): string => encodeURIComponent(s);

  return {
    /** The review queue. Defaults to everything a human still has to touch. */
    queue: (
      query?: { locale?: string; status?: TranslationStatus; entityType?: TranslationEntityType; limit?: number },
      o?: Extra,
    ): Promise<TranslationQueueResponse> =>
      client.get<TranslationQueueResponse>('/v1/translations', { ...extra(o), query: query ?? {} }),

    /** Vietnamese source + every locale, side by side. */
    detail: (
      entityType: TranslationEntityType,
      entityId: string,
      o?: Extra,
    ): Promise<TranslationDetail> =>
      client.get<TranslationDetail>(`/v1/translations/${seg(entityType)}/${seg(entityId)}`, extra(o)),

    edit: (
      entityType: TranslationEntityType,
      entityId: string,
      locale: string,
      body: TranslationEditRequest,
      o?: Extra,
    ): Promise<TranslationEditResponse> =>
      client.put<TranslationEditResponse>(
        `/v1/translations/${seg(entityType)}/${seg(entityId)}/${seg(locale)}`,
        { ...extra(o), body, idempotent: false },
      ),

    approve: (
      entityType: TranslationEntityType,
      entityId: string,
      locale: string,
      o?: Extra,
    ): Promise<TranslationWorkflowResponse> =>
      client.post<TranslationWorkflowResponse>(
        `/v1/translations/${seg(entityType)}/${seg(entityId)}/${seg(locale)}/approve`,
        { ...extra(o), idempotent: false },
      ),

    /** Requires a reason (`x-action-reason`). */
    reject: (
      entityType: TranslationEntityType,
      entityId: string,
      locale: string,
      reason: string,
      o?: Extra,
    ): Promise<TranslationWorkflowResponse> =>
      client.post<TranslationWorkflowResponse>(
        `/v1/translations/${seg(entityType)}/${seg(entityId)}/${seg(locale)}/reject`,
        { ...extra(o), reason, idempotent: false },
      ),

    publish: (
      entityType: TranslationEntityType,
      entityId: string,
      locale: string,
      o?: Extra,
    ): Promise<TranslationWorkflowResponse> =>
      client.post<TranslationWorkflowResponse>(
        `/v1/translations/${seg(entityType)}/${seg(entityId)}/${seg(locale)}/publish`,
        { ...extra(o), idempotent: false },
      ),

    /** Requires a reason. `toVersion` is a previous `translationVersion`. */
    rollback: (
      entityType: TranslationEntityType,
      entityId: string,
      locale: string,
      toVersion: number,
      reason: string,
      o?: Extra,
    ): Promise<TranslationWorkflowResponse> =>
      client.post<TranslationWorkflowResponse>(
        `/v1/translations/${seg(entityType)}/${seg(entityId)}/${seg(locale)}/rollback`,
        { ...extra(o), body: { toVersion }, reason, idempotent: false },
      ),

    /** Enqueues AI jobs; it never translates inline. */
    generate: (body: TranslationGenerateRequest, o?: Extra): Promise<TranslationGenerateResponse> =>
      client.post<TranslationGenerateResponse>('/v1/translations/generate', {
        ...extra(o),
        body,
        idempotent: false,
      }),

    listGlossary: (
      query?: { locale?: string; termType?: string },
      o?: Extra,
    ): Promise<{ terms: GlossaryTerm[] }> =>
      client.get<{ terms: GlossaryTerm[] }>('/v1/translations/glossary', { ...extra(o), query: query ?? {} }),

    createGlossaryTerm: (body: GlossaryTermInput, o?: Extra): Promise<{ ok: boolean; id: string }> =>
      client.post<{ ok: boolean; id: string }>('/v1/translations/glossary', {
        ...extra(o),
        body,
        idempotent: false,
      }),

    updateGlossaryTerm: (
      id: string,
      body: GlossaryTermInput,
      o?: Extra,
    ): Promise<{ ok: boolean; id: string }> =>
      client.put<{ ok: boolean; id: string }>(`/v1/translations/glossary/${seg(id)}`, {
        ...extra(o),
        body,
        idempotent: false,
      }),

    deleteGlossaryTerm: (id: string, o?: Extra): Promise<{ ok: boolean; id: string }> =>
      client.del<{ ok: boolean; id: string }>(`/v1/translations/glossary/${seg(id)}`, {
        ...extra(o),
        idempotent: false,
      }),

    listProviders: (o?: Extra): Promise<{ providers: TranslationProvider[] }> =>
      client.get<{ providers: TranslationProvider[] }>('/v1/translation-providers', extra(o)),

    createProvider: (body: TranslationProviderInput, o?: Extra): Promise<Record<string, unknown>> =>
      client.post<Record<string, unknown>>('/v1/translation-providers', {
        ...extra(o),
        body,
        idempotent: false,
      }),

    updateProvider: (
      id: string,
      body: TranslationProviderInput,
      o?: Extra,
    ): Promise<Record<string, unknown>> =>
      client.put<Record<string, unknown>>(`/v1/translation-providers/${seg(id)}`, {
        ...extra(o),
        body,
        idempotent: false,
      }),

    /** Soft-disable: the API sets `is_active = 0` rather than deleting. */
    disableProvider: (id: string, o?: Extra): Promise<{ ok: boolean; id: string; isActive: boolean }> =>
      client.del<{ ok: boolean; id: string; isActive: boolean }>(`/v1/translation-providers/${seg(id)}`, {
        ...extra(o),
        idempotent: false,
      }),

    listLocales: (query?: { branchId?: string }, o?: Extra): Promise<LocalesResponse> =>
      client.get<LocalesResponse>('/v1/locales', { ...extra(o), query: query ?? {} }),

    setTenantLocale: (
      locale: string,
      body: TenantLocaleInput,
      o?: Extra,
    ): Promise<{ ok: boolean; scope: string; locale: string }> =>
      client.put(`/v1/locales/tenant/${seg(locale)}`, { ...extra(o), body, idempotent: false }),

    setBranchLocale: (
      branchId: string,
      locale: string,
      body: BranchLocaleInput,
      o?: Extra,
    ): Promise<{ ok: boolean; scope: string; branchId: string; locale: string }> =>
      client.put(`/v1/locales/branch/${seg(branchId)}/${seg(locale)}`, {
        ...extra(o),
        body,
        idempotent: false,
      }),
  };
}

// ===========================================================================
// customer QR surface  (routes/customer.ts) — public, session-token scoped.
// Included for the POS/Staff "xem như khách" preview; the Admin app uses
// `menuApi.customerPreview` instead because it is already authenticated.
// ===========================================================================

export function customerApi(client: ApiClient) {
  const withToken = (token: string, o?: Extra): RequestOptions => ({
    ...extra(o),
    anonymous: true,
    headers: { 'x-session-token': token },
  });

  return {
    session: (token: string, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/customer/session', withToken(token, o)),
    setLocale: (token: string, locale: string, o?: Extra): Promise<Record<string, unknown>> =>
      client.post<Record<string, unknown>>('/v1/customer/session/locale', {
        ...withToken(token, o),
        body: { locale },
        idempotent: false,
      }),
    menu: (token: string, query?: { locale?: string }, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/customer/menu', { ...withToken(token, o), query: query ?? {} }),
    uiStrings: (token: string, query?: { locale?: string }, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/customer/ui-strings', {
        ...withToken(token, o),
        query: query ?? {},
      }),
    orders: (token: string, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/customer/orders', withToken(token, o)),
    requests: (token: string, o?: Extra): Promise<Record<string, unknown>> =>
      client.get<Record<string, unknown>>('/v1/customer/requests', withToken(token, o)),
  };
}

// ===========================================================================
// One object, all domains.
// ===========================================================================

export interface SonautoApi {
  readonly meta: ReturnType<typeof metaApi>;
  readonly auth: ReturnType<typeof authApi>;
  readonly org: ReturnType<typeof orgApi>;
  readonly menu: ReturnType<typeof menuApi>;
  readonly orders: ReturnType<typeof ordersApi>;
  readonly payments: ReturnType<typeof paymentsApi>;
  readonly production: ReturnType<typeof productionApi>;
  readonly staff: ReturnType<typeof staffApi>;
  readonly translation: ReturnType<typeof translationApi>;
  readonly customer: ReturnType<typeof customerApi>;
  readonly client: ApiClient;
}

export function createApi(client: ApiClient): SonautoApi {
  return {
    meta: metaApi(client),
    auth: authApi(client),
    org: orgApi(client),
    menu: menuApi(client),
    orders: ordersApi(client),
    payments: paymentsApi(client),
    production: productionApi(client),
    staff: staffApi(client),
    translation: translationApi(client),
    customer: customerApi(client),
    client,
  };
}
